import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  formatPercent,
  toNumber
} from './panel-utils.js';
import { createSvg, clearSvg, createSvgEl } from './chart-utils.js';

const DEFAULT_LINEAGE = {
  nodes: [
    { id: 'raw_events', name: 'raw_events', stage: 0, freshness: 0.92 },
    { id: 'session_clean', name: 'session_clean', stage: 1, freshness: 0.84 },
    { id: 'feature_store', name: 'feature_store', stage: 2, freshness: 0.78 },
    { id: 'model_v4', name: 'model_v4', stage: 3, freshness: 0.65 }
  ],
  edges: [
    { from: 'raw_events', to: 'session_clean' },
    { from: 'session_clean', to: 'feature_store' },
    { from: 'feature_store', to: 'model_v4' }
  ]
};

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Data Lineage Graph');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const graphWrap = document.createElement('div');
  graphWrap.className = 'ew-graph-canvas';
  const graphSvg = createSvg(320, 140);
  graphWrap.appendChild(graphSvg);

  const list = document.createElement('div');
  list.className = 'ew-list';

  body.append(metrics, graphWrap, list);

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Data Lineage Graph');
    const lineage = next?.data?.lineage || next?.props?.lineage || DEFAULT_LINEAGE;
    const nodes = Array.isArray(lineage.nodes) ? lineage.nodes : DEFAULT_LINEAGE.nodes;
    const edges = Array.isArray(lineage.edges) ? lineage.edges : DEFAULT_LINEAGE.edges;

    metaEl.textContent = `${nodes.length} assets`;
    const avgFresh = nodes.length
      ? nodes.reduce((acc, n) => acc + toNumber(n.freshness, 0), 0) / nodes.length
      : 0;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('assets', formatNumber(nodes.length)),
      createMetric('links', formatNumber(edges.length)),
      createMetric('freshness', formatPercent(avgFresh, 0))
    );

    renderLineage(graphSvg, nodes, edges);

    list.innerHTML = '';
    nodes.slice(0, 8).forEach((asset) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ew-list-item';
      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const name = document.createElement('span');
      name.className = 'ew-list-title';
      name.textContent = asset.name;
      const tag = document.createElement('span');
      tag.className = 'ew-tag';
      tag.textContent = `stage ${asset.stage + 1}`;
      head.append(name, tag);
      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      meta.textContent = `freshness ${formatPercent(toNumber(asset.freshness, 0), 0)}`;
      item.append(head, meta);
      item.addEventListener('click', () => {
        api.emit('node:input', { id: node.id, field: 'asset', value: asset.id });
      });
      list.appendChild(item);
    });
  };

  update(node);
  return { el: root, update };
}

function renderLineage(svg, nodes, edges) {
  clearSvg(svg);
  if (!nodes.length) return;
  const width = 320;
  const height = 140;
  const pad = 16;
  const stages = Array.from(new Set(nodes.map((n) => toNumber(n.stage, 0)))).sort((a, b) => a - b);
  const stageCount = stages.length || 1;
  const gapX = (width - pad * 2) / Math.max(1, stageCount - 1);

  const stageMap = new Map();
  stages.forEach((stage, idx) => stageMap.set(stage, idx));

  const positions = new Map();
  const stageBuckets = stages.map(() => []);
  nodes.forEach((node) => {
    const idx = stageMap.get(toNumber(node.stage, 0)) || 0;
    stageBuckets[idx].push(node);
  });

  stageBuckets.forEach((bucket, idx) => {
    const gapY = (height - pad * 2) / Math.max(1, bucket.length - 1 || 1);
    bucket.forEach((node, row) => {
      const x = pad + idx * gapX;
      const y = pad + row * gapY;
      positions.set(node.id, { x, y });
    });
  });

  edges.forEach((edge) => {
    const from = positions.get(edge.from);
    const to = positions.get(edge.to);
    if (!from || !to) return;
    const line = createSvgEl('line', {
      x1: from.x,
      y1: from.y,
      x2: to.x,
      y2: to.y,
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': 0.5
    });
    svg.appendChild(line);
  });

  nodes.forEach((node) => {
    const pos = positions.get(node.id);
    const rect = createSvgEl('rect', {
      x: pos.x - 18,
      y: pos.y - 8,
      width: 36,
      height: 16,
      rx: 4,
      fill: 'var(--ew-ui-code-bg)',
      stroke: 'var(--ew-ui-accent)',
      'stroke-opacity': 0.6
    });
    svg.appendChild(rect);
    const label = createSvgEl('text', {
      x: pos.x,
      y: pos.y + 3,
      fill: 'var(--ew-ui-text)',
      'font-size': '8px',
      'text-anchor': 'middle'
    });
    label.textContent = node.name.slice(0, 6);
    svg.append(rect, label);
  });
}
